import { FC, useMemo, useState } from 'react';

import { DBUser } from '@april9au/stack9-sdk';
import * as ui from '@april9au/stack9-ui';
import { message, Modal, Select, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';

const Table = ui.S9Table.default;
const S9Button = ui.S9Button.default;

type PermissionSetUser = Pick<
  DBUser,
  'id' | 'full_name' | 'first_name' | 'last_name' | 'email'
>;

type PermissionSetUsersGridProps = {
  permissionSetId: number;
  onRefresh?: () => void;
};

export const PermissionSetUsersGrid: FC<PermissionSetUsersGridProps> = ({
  permissionSetId,
  onRefresh,
}) => {
  const { handleSave } = ui.useEntityActions('permission_set');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);

  const {
    data: queryData,
    mutate,
    isValidating: isLoading,
  } = ui.useScreenQuery<{
    results: Array<{ id: number; users?: PermissionSetUser[] }>;
  }>('permission_set_user_list', {
    vars: { permissionSetId: String(permissionSetId) },
  });
  const rows = useMemo(() => queryData?.results?.[0]?.users ?? [], [queryData]);
  const currentIds = useMemo(() => rows.map(r => r.id), [rows]);

  const { data: userData } = ui.useScreenQuery<{
    results: PermissionSetUser[];
  }>('user_list_dropdown', { vars: {} });
  const availableUsers = useMemo(() => {
    const assigned = new Set(currentIds);
    return (userData?.results ?? []).filter(u => !assigned.has(u.id));
  }, [userData, currentIds]);

  /**
   * Persists the permission set's user membership as the given id list. Uses
   * the entity actions save (a partial update), so only the `users` relation
   * is synced — the set's other fields are left untouched.
   */
  const saveMembership = async (userIds: number[]) => {
    await handleSave({ users: userIds }, permissionSetId, () => {});
    mutate();
    onRefresh?.();
  };

  const handleAdd = () => {
    setSelectedUserId(undefined);
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    if (!selectedUserId) {
      message.error('Select a user to add');
      return;
    }
    setLoading(true);
    try {
      await saveMembership([...currentIds, selectedUserId]);
      message.success('User added to permission set');
      setIsModalVisible(false);
      setSelectedUserId(undefined);
    } catch (error: unknown) {
      message.error(`Failed to add user: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (user: PermissionSetUser) => {
    Modal.confirm({
      title: 'Remove User',
      content: `Remove ${
        user.full_name ?? user.email ?? `#${user.id}`
      } from this permission set?`,
      okText: 'Remove',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          await saveMembership(currentIds.filter(id => id !== user.id));
          message.success('User removed from permission set');
        } catch (error: unknown) {
          message.error(`Failed to remove user: ${(error as Error).message}`);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns: ColumnsType<PermissionSetUser> = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (v: string, record) =>
        v ||
        [record.first_name, record.last_name].filter(Boolean).join(' ') ||
        '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v: string) => v || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space>
          <S9Button
            type="link"
            danger
            icon="DeleteOutlined"
            onClick={() => handleRemove(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-medium">Users</h3>
        <S9Button
          type="primary"
          icon="PlusOutlined"
          onClick={handleAdd}
          label="Add User"
        />
      </div>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={isLoading || loading}
        pagination={false}
      />

      <Modal
        title="Add User"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedUserId(undefined);
        }}
        confirmLoading={loading}
        width={520}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a user"
          showSearch
          allowClear
          value={selectedUserId}
          onChange={value => setSelectedUserId(value)}
          options={availableUsers.map(u => ({
            value: u.id,
            label: `${u.full_name ?? '-'}${u.email ? ` (${u.email})` : ''}`,
          }))}
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      </Modal>
    </div>
  );
};
